import ProjectWorld, { LATEST_PROJECTION_VERSION, ProjectEventType } from "@/ProjectWorld";
import { inject, injectable } from "tsyringe";
import { TOKEN_IProjectWorldReader, type IProjectWorldReader } from "./ProjectWorldReader";
import { TOKEN_IProjectWorldWriter, type IProjectWorldWriter } from "./ProjectWorldWriter";
import { hexStringWithFloatAlphaToNumber, randomColor, sleep } from "@/lib/utils";
import { TOKEN_IProjectWorldCache, type IProjectWorldCache } from "./ProjectWorldCache";
import { type IWrapId, TOKEN_IWrapId } from "./IWrapId";
import { type IProjectEventGenerator, TOKEN_IProjectEventGenerator } from "./ProjectEventGenerator";
import { ErrorForbidden, ErrorNotExist, TOKEN_IServerCalls, type IServerCalls } from "./ServerCalls";
import { EncryptedProjectError, type IProjectSyncing, TOKEN_IProjectSyncing } from "./ProjectSyncing";
import { ServerIssueType, TOKEN_IProjectBus, type IProjectBus } from "./ProjectBus";
import log from "loglevel";
import { TOKEN_IWrapTime, type IWrapTime } from "./WrapTime";
import { TOKEN_IUserIdentity, type IUserIdentity } from "./UserIdentity";
import { TOKEN_IWrapAuth0, type IWrapAuth0 } from "./WrapAuth0";
import { EncryptedProject, encryptedProject } from "@utilitydelta/nosignup-js";

export const TOKEN_IWorldBuilder = Symbol('IWorldBuilder');


export interface SyncResult {
    projectWorld: ProjectWorld;
    performedServerSync: boolean;
}

export interface IWorldBuilder {
    mustLoadFromServerIfNotLocal(projectId: string, shareKey: string | undefined, symmetricKey: string | undefined): Promise<SyncResult>;
    createLocalProject(gridSize: number): Promise<string>;
    iterativeHistoryPlay(projectId: string, isCancelled: (position: number) => boolean): Promise<void>;
    forkProject(projectId: string): Promise<string>;
}

@injectable()
export class WorldBuilder implements IWorldBuilder {

    constructor(
        @inject(TOKEN_IProjectWorldReader) private projectWorldReader: IProjectWorldReader,
        @inject(TOKEN_IProjectWorldWriter) private projectWorldWriter: IProjectWorldWriter,
        @inject(TOKEN_IProjectWorldCache) private projectWorldCache: IProjectWorldCache,
        @inject(TOKEN_IWrapId) private wrapId: IWrapId,
        @inject(TOKEN_IWrapTime) private wrapTime: IWrapTime,
        @inject(TOKEN_IWrapAuth0) private wrapAuth0: IWrapAuth0,
        @inject(TOKEN_IUserIdentity) private userIdentity: IUserIdentity,
        @inject(TOKEN_IProjectEventGenerator) private projectEventGenerator: IProjectEventGenerator,
        @inject(TOKEN_IServerCalls) private serverCalls: IServerCalls,
        @inject(TOKEN_IProjectSyncing) private projectSyncing: IProjectSyncing,
        @inject(TOKEN_IProjectBus) private projectBus: IProjectBus,
    ) { }

    createLocalProject = async (gridSize: number): Promise<string> => {
        const world = await this.maybeCreateProject(this.wrapId.generate(), true);

        await this.projectEventGenerator.createColorSquare(world.projectMetadata.id, gridSize);
        await this.projectEventGenerator.assignRandomUsername(world.projectMetadata.id);

        await this.projectSyncing.optInForServerIfLoggedIn(world.projectMetadata.id);
        if (world.projectMetadata.userOptInForServer) {
            await this.projectSyncing.writeToServer(world.projectMetadata.id);
        }

        return world.projectMetadata.id;
    }

    private async maybeCreateProject(projectId: string, generateNewSymmetricKey: boolean, symmetricKeyOverride?: string): Promise<ProjectWorld> {
        let world = await this.lazyMaybeGetProject(projectId); //Critial to call this.lazyMaybeGetProject so we can reproject if needed
        if (world !== undefined) {
            return world;
        }

        const clientId = (await this.userIdentity.publicKey()).publicKeyHashed;
        const userId = this.wrapAuth0.userIdHashed;
        const nowUtcEpoch = this.wrapTime.getCurrentUnixTimestamp();

        let ep: EncryptedProject | undefined = undefined;
        let symmetricKey: string | undefined = undefined;

        if (generateNewSymmetricKey || symmetricKeyOverride !== undefined) {
            ep = await encryptedProject(symmetricKeyOverride);
            symmetricKey = ep.getSymmetricKey()!;
        }

        world = new ProjectWorld({
            id: projectId,
            isDeleted: false,
            createdByClientId: clientId,
            createdByUserId: userId,
            createdOn: nowUtcEpoch,
            modifiedByClientId: clientId,
            modifiedByUserId: userId,
            modifiedOn: nowUtcEpoch,
            numberOfEvents: 0,
            enforceAggregateConcurrency: false,
            sentServerEventsUpTo: 0,
            serverEventsUpTo: 0,
            userOptInForServer: false,
            projectionVersion: LATEST_PROJECTION_VERSION,
            myColor: hexStringWithFloatAlphaToNumber(randomColor(), 1.0),
            gridSize: 0,
            symmetricKey,
        });

        await this.projectWorldWriter.saveProjectWorldMetadata(world.projectMetadata);

        return await this.projectWorldReader.mustGet(projectId);
    }

    private async lazyMaybeGetProject(projectId: string): Promise<ProjectWorld | undefined> {
        const world = await this.projectWorldReader.lazyMaybeGet(projectId);
        if (world === undefined) return undefined;

        if (world.projectMetadata.projectionVersion === LATEST_PROJECTION_VERSION) return world;

        const newWorld = new ProjectWorld(world.projectMetadata);
        if (world.projectMetadata.symmetricKey !== undefined) {
            newWorld.encryptedProject = await encryptedProject(world.projectMetadata.symmetricKey);
        }

        const allEvents = await this.projectWorldReader.allEvents(projectId);
        newWorld.reprojectAllEvents(allEvents, LATEST_PROJECTION_VERSION);

        await this.projectWorldWriter.replaceWorld(world, newWorld);
        this.projectWorldCache.upsert(newWorld);

        return newWorld;
    }

    forkProject = async (projectId: string): Promise<string> => {
        const isSouceProjectExist = await this.projectWorldReader.isExistsLocally(projectId);
        if (!isSouceProjectExist) {
            throw new Error(`Project ${projectId} does not exist locally`);
        }
        const sourceProjectWorld = await this.projectWorldReader.lazyMaybeGet(projectId);
        if (!sourceProjectWorld) {
            throw new Error(`Project ${projectId} does not exist locally`);
        }

        const targetProjectId = await this.createLocalProject(sourceProjectWorld.projectMetadata.gridSize);

        await Promise.all(sourceProjectWorld.coloredSquares.allNotDeleted.map((square) => this.projectEventGenerator.setSpecificColor(targetProjectId, square.x, square.y, square.color)));

        return targetProjectId;
    }

    mustLoadFromServerIfNotLocal = async (projectId: string, shareKey: string | undefined, symmetricKey: string | undefined
    ): Promise<SyncResult> => {
        let projectWorld = await this.lazyMaybeGetProject(projectId);

        // If project exists locally, just return it
        if (projectWorld !== undefined) {

            await this.projectSyncing.optInForServerIfLoggedIn(projectWorld.projectMetadata.id);

            if (projectWorld.projectMetadata.symmetricKey === undefined && symmetricKey !== undefined) {
                projectWorld.projectMetadata.symmetricKey = symmetricKey;
            }

            if (projectWorld.projectMetadata.userOptInForServer) {
                await this.projectSyncing.writeToServer(projectWorld.projectMetadata.id);
            }

            this.projectSyncing.readFromServer(projectWorld.projectMetadata.id);

            return {
                projectWorld,
                performedServerSync: false
            };
        }

        try {
            // this will throw 403 or 404
            const eventBatches = await this.serverCalls.readEvents(
                projectId,
                0, // Start from beginning
                shareKey,
                true // Load own events handles removal of project from local and then re-load
            );


            projectWorld = await this.maybeCreateProject(projectId, false, symmetricKey);

            try {
                await this.projectSyncing.processServerEvents(projectWorld, eventBatches, false);
                await this.projectEventGenerator.joinedUsingShareLink(projectId, shareKey);
                await this.projectEventGenerator.assignRandomUsername(projectId);
            } catch (error: unknown) {
                if (error instanceof EncryptedProjectError) {
                    log.error('Client does not have symmetric key to decrypt events', projectId, error);
                    await this.projectEventGenerator.askForSymmetricKey(projectId);
                    this.projectBus.serverIssue(ServerIssueType.PendingDecryption);
                } else {
                    throw error;
                }
            }

            return { projectWorld, performedServerSync: true };
        } catch (error: unknown) {
            if (error instanceof ErrorForbidden) {
                log.error('Access forbidden to project.', projectId);
                this.projectBus.serverIssue(ServerIssueType.NoPermission);
            } else if (error instanceof ErrorNotExist) {
                log.error('Project not found on server', projectId);
                this.projectBus.serverIssue(ServerIssueType.NotFound);
            } else {
                log.error('Error reading events from server', projectId, error);
                this.projectBus.serverIssue(ServerIssueType.ServerError);
            }
            throw error;
        }
    }

    iterativeHistoryPlay = async (projectId: string, isCancelled: (position: number) => boolean) => {
        const projectWorld = await this.projectWorldReader.mustGet(projectId);
        const allEvents = await this.projectWorldReader.allEvents(projectId);
        log.info("Loading all events from scratch: ", allEvents.length);

        const world = new ProjectWorld(projectWorld.projectMetadata);
        if (world.projectMetadata.symmetricKey !== undefined) {
            world.encryptedProject = await encryptedProject(world.projectMetadata.symmetricKey);
        }

        let position = 0;
        for (const event of allEvents) {
            position++;
            if (isCancelled(position)) return;

            if (event.tp === ProjectEventType.SetColor) {
                await sleep(1);
            }
            const processor = world.eventProcessors.getProcessor(event.tp);
            const projectionResult = processor!.projectOntoWorld(event, false, true);
            if (projectionResult) {
                this.projectBus.projectionUpdated(projectId, projectionResult);
            }
        }
    }
}